import { Component, EventEmitter, OnInit } from '@angular/core';
import {
  DwButtonCommand,
  DwButtonConfig,
  DwModalConfig,
  DwModalHandler,
  DwModalResult,
  DwSecurityUserService
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';

import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import {
  BunnyPlayerTestMode,
  BunnyPlayerTestSettings,
  BunnyPlayerTestSkipMode
} from '../../models/bunny-player-test-settings.model';
import { BunnyPlayerTestSettingsService } from '../../services/bunny-player-test-settings.service';

interface BunnyPlayerModeOption {
  value: BunnyPlayerTestMode;
  label: string;
  description: string;
}

interface BunnyPlayerSkipOption {
  value: BunnyPlayerTestSkipMode;
  label: string;
  description: string;
}

@Component({
  selector: 'app-bunny-player-test-settings-modal',
  templateUrl: './bunny-player-test-settings-modal.component.html',
  styleUrls: ['./bunny-player-test-settings-modal.component.scss']
})
export class BunnyPlayerTestSettingsModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<any>;
  closeModal = new EventEmitter<DwModalResult<any>>();

  readonly modeOptions: BunnyPlayerModeOption[] = [
    {
      value: 'automatic',
      label: 'Automatic',
      description: 'Use the player selected by the video library playback mode.'
    },
    {
      value: 'bunny',
      label: 'Bunny Player',
      description: 'Force the existing Bunny embedded player when a legacy source is available.'
    },
    {
      value: 'vidstack',
      label: 'Vidstack Player',
      description: 'Force the signed HLS source and the new Vidstack player.'
    }
  ];

  mode: BunnyPlayerTestMode = 'automatic';
  readonly skipOptions: BunnyPlayerSkipOption[];
  skipMode: BunnyPlayerTestSkipMode = 'default';
  showDiagnostics = false;

  constructor(
    private settingsService: BunnyPlayerTestSettingsService,
    dwSecurityUserService: DwSecurityUserService
  ) {
    const userCanSeek = dwSecurityUserService.hasPermission(AppPermissions.videoCanSeek);
    this.skipOptions = [
      {
        value: 'default',
        label: `Default (${userCanSeek ? 'allow skipping' : 'prevent skipping'})`,
        description: 'Use the normal application rule for the current user.'
      },
      {
        value: 'allow',
        label: 'Allow skipping',
        description: 'Allow seeking anywhere in the video for this test session.'
      },
      {
        value: 'prevent',
        label: 'Prevent skipping',
        description: 'Prevent seeking ahead for this test session.'
      },
      {
        value: 'completed',
        label: 'Completed lessons only',
        description: 'Allow skipping only when the current lesson was already completed.'
      }
    ];
  }

  ngOnInit(): void {
    const current = this.settingsService.current;
    this.mode = current.mode;
    this.skipMode = current.skipMode;
    this.showDiagnostics = current.showDiagnostics;
  }

  selectMode(mode: BunnyPlayerTestMode): void {
    this.mode = mode;
  }

  selectSkipMode(skipMode: BunnyPlayerTestSkipMode): void {
    this.skipMode = skipMode;
  }

  selectDiagnostics(showDiagnostics: boolean): void {
    this.showDiagnostics = showDiagnostics;
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<BunnyPlayerTestSettings>> {
    const result = new DwModalResult<BunnyPlayerTestSettings>();

    switch (buttonConfig.command) {
      case DwButtonCommand.ok:
      case DwButtonCommand.save:
      case DwButtonCommand.saveAndClose:
        const settings: BunnyPlayerTestSettings = {
          mode: this.mode,
          skipMode: this.skipMode,
          showDiagnostics: this.showDiagnostics
        };
        this.settingsService.save(settings);
        result.data = settings;
        result.closeModal = true;
        result.cancelled = false;
        break;

      case DwButtonCommand.cancel:
        result.closeModal = true;
        result.cancelled = true;
        break;
    }

    return of(result);
  }
}
